import React, { useState, useEffect } from 'react';
import { ChevronRight, Shield } from 'lucide-react';

interface HomeProps {
  setCurrentPage: (page: string) => void;
}

const Home: React.FC<HomeProps> = ({ setCurrentPage }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    {
      id: 1,
      title: "Strategic Leadership",
      description: "Expert guidance in cybersecurity strategy and execution",
      details: [
        "Virtual CISO Services",
        "Security Program Development",
        "Risk Management",
        "Compliance Oversight"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 bg-gradient-to-br from-purple-700 to-blue-500 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className={`text-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-block px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-6">
              <span className="text-sm font-medium">Leading Cybersecurity Excellence</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Secured with Grace
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              Elevating enterprise cybersecurity through innovation, expertise, and unwavering commitment to digital resilience
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('contact')}
                className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-blue-500 hover:bg-blue-600 transition-colors"
              >
                Get Started <ChevronRight className="ml-2" />
              </button>
              <button
                onClick={() => setCurrentPage('services')}
                className="inline-flex items-center px-8 py-4 border-2 border-white text-lg font-medium rounded-md text-white hover:bg-white hover:text-purple-700 transition-colors"
              >
                Explore Solutions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Grace Technologies</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine deep industry expertise, advanced technology, and proven methodologies
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature) => (
              <div 
                key={feature.id}
                className="bg-gray-50 p-8 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-xl"
                onMouseEnter={() => setSelectedFeature(feature.id)}
                onMouseLeave={() => setSelectedFeature(null)}
              >
                <Shield className={`w-16 h-16 mb-6 text-purple-700 transition-transform duration-300 ${selectedFeature === feature.id ? 'scale-110' : ''}`} />
                <h3 className="text-2xl font-semibold mb-4">{feature.title}</h3>
                <p className="text-gray-600 mb-6">{feature.description}</p>
                <ul className={`space-y-3 transition-all duration-300 ${selectedFeature === feature.id ? 'opacity-100' : 'opacity-0 h-0'}`}>
                  {feature.details.map((detail, index) => (
                    <li key={index} className="flex items-center text-gray-600">
                      <div className="w-2 h-2 bg-purple-700 rounded-full mr-3"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;